// components/SourcesPanel.js
//
// Displays the curated, verified medical sources relevant to the current
// Care Copilot context (or lab analysis). Addresses App Store Guideline
// 1.4.1 — citations must exist and be easy to find. This is a persistent,
// clearly-visible button, not buried in a settings menu.
//
// Usage:
//   <SourcesPanel contextId={activeContext.id} />       // inside Care Copilot
//   <SourcesPanel sources={DEFAULT_SOURCES} label="Lab result sources" />  // lab analysis screen

import React, { useState } from 'react';
import { getSourcesForContext } from '../data/carecopilotSources';

const SourcesPanel = ({ contextId, sources, label = 'Sources' }) => {
  const [open, setOpen] = useState(false);
  const resolvedSources = sources || getSourcesForContext(contextId);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        title="Sources & References"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: label ? 6 : 0,
          padding: label ? '6px 12px' : '6px 8px',
          borderRadius: 8,
          background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.18)',
          color: 'rgba(255,255,255,0.75)',
          fontSize: label ? 12 : 14,
          fontWeight: 600,
          cursor: 'pointer',
          fontFamily: "'DM Sans', system-ui, sans-serif",
          lineHeight: 1,
        }}
      >
        📚{label ? ` ${label}` : ''}
      </button>

      {open && (
        <>
          {/* Click-outside overlay */}
          <div
            onClick={() => setOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 998 }}
          />
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              right: 0,
              width: 320,
              maxHeight: 360,
              overflowY: 'auto',
              background: '#0D2B45',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 12,
              padding: 16,
              zIndex: 999,
              boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: 'white', marginBottom: 4 }}>
              Sources & References
            </div>
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 12, lineHeight: 1.5 }}>
              Care Copilot draws on established medical resources. It does not replace advice from
              your doctor or care team.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {resolvedSources.map((source) => (
                <a
                  key={source.url}
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'block',
                    padding: '8px 10px',
                    borderRadius: 8,
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: '#7fd8e6',
                    fontSize: 12.5,
                    fontWeight: 600,
                    textDecoration: 'none',
                    lineHeight: 1.4,
                  }}
                >
                  {source.name}
                </a>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SourcesPanel;